import type { CSSProperties, ReactNode } from "react";
import { Check } from "lucide-react";
import type { Fit } from "./assessment";

/** The dotted "blueprint" paper behind the answers board. */
export const blueprintPaper: CSSProperties = {
  backgroundColor: "#f5f9fd",
  backgroundImage: "radial-gradient(#cddff0 1px, transparent 1px)",
  backgroundSize: "18px 18px",
};

export const primaryButton =
  "inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 font-semibold text-text-inverse shadow-lg shadow-primary/20 transition-colors hover:bg-primaryHover disabled:cursor-not-allowed disabled:opacity-50";
export const secondaryButton =
  "inline-flex items-center justify-center gap-2 rounded-full border border-borderSubtle bg-white px-6 py-3 font-semibold text-text-primary transition-colors hover:bg-surfaceSoft";
export const textButton =
  "inline-flex min-h-[44px] items-center gap-2 px-2 font-semibold text-primary underline-offset-4 hover:underline";

export function Chip({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={pressed} onClick={onClick}
            className={`inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 px-4 text-base font-semibold transition-colors ${
              pressed ? "border-primary bg-primary/10 text-text-primary" : "border-borderSubtle bg-white text-text-primary hover:border-primary/50"}`}>
      {pressed && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function OptionCard({ pressed, multi, label, hint, onClick }: {
  pressed: boolean; multi?: boolean; label: string; hint?: string; onClick: () => void;
}) {
  return (
    <button type="button" aria-pressed={pressed} onClick={onClick}
            className={`flex min-h-[68px] items-center gap-4 rounded-2xl border-2 px-5 py-3 text-left transition-colors ${
              pressed ? "border-primary bg-primary/10" : "border-borderSubtle bg-white hover:border-primary/50"}`}>
      <span aria-hidden="true"
            className={`flex h-6 w-6 flex-none items-center justify-center border-2 text-white ${multi ? "rounded-md" : "rounded-full"} ${
              pressed ? "border-primary bg-primary" : "border-slate-400 bg-white"}`}>
        {pressed && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-lg font-semibold text-text-primary">{label}</span>
        {hint && <span className="text-sm text-text-muted">{hint}</span>}
      </span>
    </button>
  );
}

const STAGES = ["Problem", "Setup", "Your queue"];

export function StageBar({ current }: { current: number }) {
  return (
    <nav aria-label="Your progress">
      <ol className="flex flex-wrap gap-2">
        {STAGES.map((label, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li key={label} aria-current={active ? "step" : undefined}
                className={`flex items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-4 text-sm font-bold ${
                  active ? "border-primary bg-primary text-white"
                    : done ? "border-primary/30 bg-primary/10 text-primary" : "border-borderSubtle bg-white text-text-muted"}`}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-extrabold ${
                active ? "bg-white text-primary" : done ? "bg-primary text-white" : "bg-slate-100 text-text-muted"}`}>
                {done ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : i + 1}
              </span>
              {label}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function FitMeter({ fit, wide }: { fit: Fit; wide?: boolean }) {
  const level = fit === "Strong" ? 3 : fit === "Good" ? 2 : 1;
  return (
    <span className="inline-flex items-center gap-2.5">
      <span className="flex gap-1" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className={`h-2 rounded-full ${wide ? "w-8" : "w-6"} ${i < level ? "bg-primary" : "bg-slate-200"}`} />
        ))}
      </span>
      <span className="font-bold text-text-primary">{fit} fit</span>
    </span>
  );
}

export function BuiltBadge() {
  return <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-extrabold text-amber-800">Built for you</span>;
}

export function InPlanBadge() {
  return <span className="catalog-pop rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-extrabold text-primary">In your plan</span>;
}

/** Respect the visitor's reduced-motion setting for timed animations. */
export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** A card that stands on its own (nothing fits, everything skipped, an empty plan): centered, with room around it. */
export function CenteredCard({ id, icon, title, children, actions }: {
  id: string; icon?: ReactNode; title: string; children: ReactNode; actions: ReactNode;
}) {
  return (
    <div role="region" aria-labelledby={id}
         className="catalog-enter mx-auto my-6 flex w-full max-w-2xl flex-col items-center gap-5 rounded-3xl border border-borderSubtle bg-surface px-6 py-10 text-center shadow-xl sm:my-10 sm:px-12 sm:py-14">
      {icon && <span aria-hidden="true" className="catalog-pop flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">{icon}</span>}
      <h2 id={id} className="mb-0 font-display text-2xl font-bold text-text-primary sm:text-3xl">{title}</h2>
      <div className="flex max-w-lg flex-col items-center gap-3 text-lg text-text-secondary">{children}</div>
      <div className="mt-2 flex flex-wrap justify-center gap-3">{actions}</div>
    </div>
  );
}
